/**
 * Block Task Modal
 *
 * Modal for blocking/unblocking project tasks with internal or external blocking types.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ProjectTask, BlockedType } from "shared/types/renovation";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { ClerkUser } from "shared/types/user";
import { Warning, LockKey } from "@phosphor-icons/react";

interface BlockTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  task: ProjectTask | null;
  onOptimisticUpdate?: (updatedTask: any) => void;
}

export const BlockTaskModal: React.FC<BlockTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  task,
  onOptimisticUpdate,
}) => {
  const { session } = useSession();
  const { user } = useUser();
  const [blockedType, setBlockedType] = useState<BlockedType>("internal");
  const [blockedReason, setBlockedReason] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState<ClerkUser | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize blocked reason with task title when modal opens
  useEffect(() => {
    if (!isOpen || !task) return;

    const defaultReason = `${task.title} wordt geblokkeerd. Geef hieronder de reden:`;
    setBlockedReason(defaultReason);
    setBlockedType("internal");
    setSelectedAssignee(null);
  }, [isOpen, task]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!task || !user) return;

    if (!blockedReason.trim()) {
      toast.error("Reden is verplicht");
      return;
    }

    if (blockedType === "internal" && !selectedAssignee) {
      toast.error("Selecteer een collega om te taggen voor interne blokkering");
      return;
    }

    // OPTIMISTIC UPDATE: Update UI immediately
    const optimisticUpdate = {
      isBlocked: true,
      blockedType,
      blockedReason: blockedReason.trim(),
      blockedBy: user.id,
      blockedAt: new Date(),
      blockedAssignedTo:
        blockedType === "internal" ? selectedAssignee?.id : undefined,
    };

    onOptimisticUpdate?.(optimisticUpdate);
    handleClose();

    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}/block`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            blockedType,
            blockedReason: blockedReason.trim(),
            blockedBy: user.id,
            blockedAssignedTo:
              blockedType === "internal" ? selectedAssignee?.id : undefined,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to block project task");
      }

      toast.success("Taak succesvol geblokkeerd!");
      onSuccess(); // Refresh kanban board
    } catch (error: any) {
      console.error("Error blocking project task:", error);
      toast.error(
        `Fout bij blokkeren taak: ${error.message || "Onbekende fout"}`,
      );

      // ROLLBACK: Revert block status on error
      onOptimisticUpdate?.({
        isBlocked: false,
        blockedType: undefined,
        blockedReason: undefined,
        blockedBy: undefined,
        blockedAt: undefined,
        blockedAssignedTo: undefined,
      });
      onSuccess(); // Force refresh
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnblock = async () => {
    if (!task || !user) return;

    // Store old state for rollback
    const oldBlockState = {
      isBlocked: task.isBlocked,
      blockedType: task.blockedType,
      blockedReason: task.blockedReason,
      blockedBy: task.blockedBy,
      blockedAt: task.blockedAt,
      blockedAssignedTo: task.blockedAssignedTo,
    };

    // OPTIMISTIC UPDATE: Update UI immediately
    onOptimisticUpdate?.({
      isBlocked: false,
      blockedType: undefined,
      blockedReason: undefined,
      blockedBy: undefined,
      blockedAt: undefined,
      blockedAssignedTo: undefined,
    });
    handleClose();

    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}/unblock`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to unblock project task");
      }

      toast.success("Taak gedeblokkeerd!");
      onSuccess(); // Refresh kanban board
    } catch (error: any) {
      console.error("Error unblocking project task:", error);
      toast.error(
        `Fout bij deblokkeren taak: ${error.message || "Onbekende fout"}`,
      );

      // ROLLBACK: Revert to old block state on error
      onOptimisticUpdate?.(oldBlockState);
      onSuccess(); // Force refresh
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setBlockedType("internal");
    setBlockedReason("");
    setSelectedAssignee(null);
    onClose();
  };

  if (!task) return null;

  // If task is already blocked, show unblock view
  if (task.isBlocked) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LockKey size={24} className="text-amber-600" />
              Taak Deblokkeren
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current block info */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <Warning size={20} className="mt-0.5 shrink-0 text-amber-600" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-amber-900">
                    Deze taak is momenteel geblokkeerd (
                    {task.blockedType === "internal" ? "Intern" : "Extern"})
                  </p>
                  {task.blockedReason && (
                    <p className="text-sm whitespace-pre-wrap text-amber-800">
                      {task.blockedReason}
                    </p>
                  )}
                  {task.blockedAt && (
                    <p className="text-xs text-amber-700">
                      Geblokkeerd op:{" "}
                      {new Date(task.blockedAt).toLocaleString("nl-NL")}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Annuleren
              </Button>
              <Button
                type="button"
                onClick={handleUnblock}
                disabled={isSubmitting}
                className="bg-moss-primary hover:bg-moss-primary/90 text-white"
              >
                {isSubmitting ? "Bezig..." : "Deblokkeren"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Block task view
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockKey size={24} className="text-amber-600" />
            Taak Blokkeren
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Blocked Type */}
          <div className="space-y-3">
            <Label>Type Blokkering</Label>
            <RadioGroup
              value={blockedType}
              onValueChange={(value) => setBlockedType(value as BlockedType)}
            >
              <div className="flex cursor-pointer items-center space-x-2 rounded-md border p-3 hover:bg-neutral-50">
                <RadioGroupItem value="internal" id="internal" />
                <Label htmlFor="internal" className="flex-1 cursor-pointer">
                  <div className="font-medium">Intern geblokkeerd</div>
                  <div className="text-sm text-neutral-600">
                    Wacht op feedback van een collega
                  </div>
                </Label>
              </div>
              <div className="flex cursor-pointer items-center space-x-2 rounded-md border p-3 hover:bg-neutral-50">
                <RadioGroupItem value="external" id="external" />
                <Label htmlFor="external" className="flex-1 cursor-pointer">
                  <div className="font-medium">Extern geblokkeerd</div>
                  <div className="text-sm text-neutral-600">
                    Wacht op externe partij of omstandigheden
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Assignee (for internal blocks) */}
          {blockedType === "internal" && (
            <div className="space-y-2">
              <Label htmlFor="assignee">
                Collega Taggen <span className="text-red-600">*</span>
              </Label>
              <p className="text-sm text-neutral-600">
                Selecteer de collega van wie je feedback nodig hebt
              </p>
              <div className="flex items-center gap-2 rounded-md border p-2">
                <UserSelection
                  defaultUser={selectedAssignee}
                  onUserSelect={setSelectedAssignee}
                />
                {selectedAssignee && (
                  <span className="text-sm text-neutral-600">
                    {selectedAssignee.first_name} {selectedAssignee.last_name}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Blocked Reason */}
          <div className="space-y-2">
            <Label htmlFor="blockedReason">
              Reden <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="blockedReason"
              value={blockedReason}
              onChange={(e) => setBlockedReason(e.target.value)}
              placeholder="Voeg de reden voor blokkering toe..."
              rows={8}
              required
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {isSubmitting ? "Bezig..." : "Blokkeren"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
